import { PlanType } from '@labrav/react-components'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { EulaType, ProductStatus } from '../modules/productsListing/types'

export type KeyValue = {
  Text: string
  Url: string | boolean | number | null | undefined
}

export interface LockMetadata {
  lockedBy?: string
  lastActivityAt?: string
  lockAcquisitionProbe?: boolean
  windowId?: string
  lockedByName?: string
  editingEnabled?: boolean
  lockByEmail?: string
}

export enum FlyoutListingProductType {
  NEW = 'NEW',
  CLONE = 'CLONE',
  MIGRATED = 'MIGRATED',
  OTHER = 'OTHER',
}

export interface FlyoutProductMigrationDetails {
  products: string[]
}

export interface FlyoutCloudProductDetails {
  productType: PlanType
  productName: string
  listingType?: FlyoutListingProductType
}

export const allowedCopyProduct = [
  ProductStatus.PRODUCT_STATUS_LIMITED,
  ProductStatus.PRODUCT_STATUS_LABRA_TESTING,
  ProductStatus.PRODUCT_STATUS_AWS_TESTING,
  ProductStatus.PRODUCT_STATUS_PUBLIC,
]

export interface NotificationRecipients {
  registrationCompleted: string[]
  privateOfferRenewalReminder: string[]
  subscribeFail: string[]
  entitlementRenew: string[]
  entitlementExpired: string[]
  entitlementUpgrade: string[]
  unsubscribePending: string[]
  unsubscribeSuccess: string[]
  privateOfferExpired: string[]
  privateOfferAccepted: string[]
  privateOfferCreated: string[]
  privateOfferExpiringSoon: string[]
  awsPrivateOfferRestrictionSuccess: string[]
  meteringSent: string[]
  awsProductListingLive: string[]
  awsProductListingRejected: string[]
  freeTrialActivated: string[]
  freeTrialDeactivated: string[]
  awsResellerAgreementCreated: string[]
  awsResellerAgreementDeactivated: string[]
  awsResellerAgreementExpiringSeller: string[]
  awsResellerAgreementExpiredSeller: string[]
}

export interface ContractDimension {
  id: number
  name: string
  label: string
  price: number | null
  price1m: number
  price12m: number
  price24m: number
  price36m: number
  labelDescription: string
  isAdditionalUsageAllowed: boolean | null
  checkBoxAdditionalUsageAllowed: boolean | null
  additionalUsagePrice: number | null
  subscriptionPrice: number | null
  additionalUsageDescription: string
  dimensionUnit?: string
  isExisting?: boolean
}

export interface FlyoutProductGeneralInformation {
  sku: string
  productLogoUrl: string
  headerImg: string
  shortProductDescription: string
  productDescription: string
  productVideoLink: string
  highlights: string[]
  supportResources: string
  resourceUrls: KeyValue[]
  productCategories: string[]
  searchKeywords: string
  eula: string
  eulaUrl: string
  refundPolicy: string
  allowedAccounts: string
  multipleDimensionsAllowed: boolean
  excludedCountries: string[]
  includedCountries: string[]
  termsAndCondition: boolean
  buyerNotificationsAllowed: boolean
  countryAvailability?: string
  countriesSelected?: string[]
  title: string
  saasUrl: string
  companyName: string
  isUsGovProduct: boolean
  hostWithLabra?: boolean
  apiEndpointUrl?: string
  apiUsageInstructions?: string
  authorizationMethod?: string[]
  apiIntegrationProtocols?: string[]
  a2aUsageInstructions?: string
  mcpUsageInstructions?: string
  isAgenticProduct?: boolean
}

export interface FreeTrialDimension {
  name: string
  productDimensionId: string
  quantity?: number
  label: string
  labelDescription: string
}

export interface FlyoutProductFreeTrial {
  isEnable: boolean
  numberOfDays: number
  eulaType: EulaType
  eulaFile?: string[]
  dimensions?: FreeTrialDimension[]
}

export interface FlyoutProductProductDimensions {
  dimensionCategoryName: string
  contractDuration: string[]
  dimensions: ContractDimension[]
  additionalDimensions?: ContractDimension[]
  formFilled: boolean
  freeTrial: FlyoutProductFreeTrial
}

export type FlyOutProductTerm = {
  label: string
  value: boolean
  type: 'checkbox' | 'radio'
  subTermsWhenTrue?: FlyOutProductTerm
  subTermsWhenFalse?: FlyOutProductTerm
}

export type FlyOutProductTermsAndConditions = Record<
  string,
  FlyOutProductTerm[]
>

export type pageMarkerType = {
  [x: string]: boolean
}

export type FlyOutProductAgreementTerm = {
  label: string
  value: boolean
  type: 'checkbox' | 'radio'
  options?: FlyOutProductTerm[]
  subTermsWhenTrue?: FlyOutProductTerm & { points: string[] }
  subTermsWhenFalse?: FlyOutProductTerm & { points: string[] }
}

export type FlyOutProductAgreement = Record<
  string,
  FlyOutProductAgreementTerm[]
>

export enum FlyoutProductAddEditSectionName {
  PAGE_MARKER = 'pageMarker',
  CLOUD_PRODUCT_DETAILS = 'productDetails',
  GENERAL_INFORMATION = 'generalInformation',
  PRODUCT_DIMENSIONS = 'productDimensions',
  LANDING_PAGE_DETAILS = 'infoCollectionFormMetaData',
  NOTIFICATIONS_RECIPIENTS = 'notificationRecipients',
  TERMS_AND_CONDITIONS = 'termsAndConditions',
  MIGRATION_DETAILS = 'migrationDetails',
  LOCK_METADATA = 'lockMetadata',
  AGREEMENT = 'agreement',
}

export interface FlyoutProductInfoCollectionFormMetaData {
  bgColor: string
  buttonColor: string
  buttonTextColor: string
  bgImageUrl: string
  redirectUrl: string
  redirectSeconds: number
  submissionSuccessMessage: string
  headingMessage: string
  privacyPolicyLink: string
  emailCustomMessage: string
  existingBuyerMessage: string
  existingBuyerRedirectSeconds: number
  existingBuyerRedirectUrl?: string
  logoUrl: string
  buyerFormFields?: FlyoutProductBuyerFormFields[]
  redirectionSettings: FlyoutProdctRedirectionSettings
}

export enum ApplicationAccessMethod {
  TESTING_CREDENTIALS = 'TESTING_CREDENTIALS',
  INSTRUCTIONS = 'INSTRUCTIONS',
  VIDEO = 'VIDEO',
  DOCUMENT = 'DOCUMENT',
}

export interface TestingCredentials {
  email: string
  password: string
}

export interface FlyoutProdctRedirectionSettings {
  isPublicFacingSignupPageAvailable: boolean
  applicationLoginPageUrl: string
  demoVideoUrl: string
  demoDocumentUrl: string
  sendDemoToBuyer: boolean
  customInstructions: string
  applicationAccessMethod: ApplicationAccessMethod
  testingCredentials: TestingCredentials
}

export interface FlyoutProductBuyerFormFields {
  id?: string
  title: string
  dataType: string
  displayOrder?: number
  isRequired: boolean
  validationType: string | null
  minLength: number
  maxLength: number
  isExisting?: boolean
  isCustom?: boolean
}

export type FlyoutProductSections = {
  [FlyoutProductAddEditSectionName.CLOUD_PRODUCT_DETAILS]: FlyoutCloudProductDetails
  [FlyoutProductAddEditSectionName.PAGE_MARKER]: pageMarkerType
  [FlyoutProductAddEditSectionName.GENERAL_INFORMATION]: FlyoutProductGeneralInformation
  [FlyoutProductAddEditSectionName.PRODUCT_DIMENSIONS]: FlyoutProductProductDimensions
  [FlyoutProductAddEditSectionName.LANDING_PAGE_DETAILS]: FlyoutProductInfoCollectionFormMetaData
  [FlyoutProductAddEditSectionName.NOTIFICATIONS_RECIPIENTS]: NotificationRecipients
  [FlyoutProductAddEditSectionName.TERMS_AND_CONDITIONS]: FlyOutProductTermsAndConditions
  [FlyoutProductAddEditSectionName.MIGRATION_DETAILS]: FlyoutProductMigrationDetails
  [FlyoutProductAddEditSectionName.LOCK_METADATA]: LockMetadata
  [FlyoutProductAddEditSectionName.AGREEMENT]: FlyOutProductAgreement
  buyerFormFields?: Record<string, BuyerFormFieldsBackend>
}

export type FlyoutMicrosoftProductSections = {
  [FlyoutProductAddEditSectionName.PAGE_MARKER]: pageMarkerType
  [FlyoutProductAddEditSectionName.GENERAL_INFORMATION]: FlyoutProductGeneralInformation
  [FlyoutProductAddEditSectionName.LANDING_PAGE_DETAILS]: FlyoutProductInfoCollectionFormMetaData
  [FlyoutProductAddEditSectionName.NOTIFICATIONS_RECIPIENTS]: NotificationRecipients
  [FlyoutProductAddEditSectionName.MIGRATION_DETAILS]: FlyoutProductMigrationDetails
  [FlyoutProductAddEditSectionName.TERMS_AND_CONDITIONS]: FlyOutProductTermsAndConditions
  buyerFormFields?: Record<string, BuyerFormFieldsBackend>
}

export interface StatusListingResObj {
  Draft: string
  Limited?: string
  'Labra-testing'?: string
  'AWS-testing'?: string
  Public?: string
  Rejected?: string
  Restricted?: string
}

export interface StatusListingItem {
  status: string
  completed?: string
  icon: IconDefinition
  color: string
  background: string
  sortOrder: number
  display?: boolean
}

export interface StatusOfferItem {
  status: string
  completed?: string
  icon: IconDefinition
  color: string
  background: string
  sortOrder: number
  display?: boolean
}

export interface PoductObjectInCreation {
  productForm: FlyoutProductSections
  microsoftProductForm: FlyoutMicrosoftProductSections
  listingType: FlyoutListingProductType
  errorOnSubmit: string
  error?: string
  updatedAt?: string
}

export interface ProductObjectDetail {
  productForm: FlyoutProductSections
  listingType?: FlyoutListingProductType
  status: string
  auditJson: StatusListingResObj
  productName: string
  edit?: boolean
  awsChangeSetStatus?: string
  error?: string
  marketplaceUrl?: string
  productType?: PlanType
  updatedAt?: string
}

export interface BuyerFormFieldsBackend {
  displayOrder: number
  title: string
  placeHolder: string
  dataType: string
  isCustom: boolean
  isRequired: boolean
  validationType: string | null
  maxLength: number
  minLength: number
}

export interface ProductDefaultInfo {
  [FlyoutProductAddEditSectionName.LANDING_PAGE_DETAILS]: FlyoutProductInfoCollectionFormMetaData
  [FlyoutProductAddEditSectionName.NOTIFICATIONS_RECIPIENTS]: NotificationRecipients
}

export interface ProductInCreation {
  currentProductInCreation: PoductObjectInCreation
  currentProductDetail: ProductObjectDetail
  productDefaultInfo?: ProductDefaultInfo
}

export enum ProductVisibility {
  PUBLIC = 'Public',
  DRAFT = 'Draft',
  LABRA_TESTING = 'Labra testing',
  LIMITED = 'Limited',
  CREATION_FAILED = 'Failed',
  AWS_TESTING = 'AWS testing',
  RESTRICTED = 'Restricted',
  REJECTED = 'Rejected',
}

export interface MigrationProduct {
  Name: string
  EntityType: string
  EntityId: string
  EntityArn: string
  LastModifiedDate: string
  Visibility: ProductVisibility
  alias?: string
  id?: string
  type?: string
}

export interface MigrationProductsData {
  rows: MigrationProduct[]
}

export interface FlyOutMigrationProducts {
  products: MigrationProductsData
}

export interface FlyOutMigrationProductsInfo {
  migrationProducts: FlyOutMigrationProducts
}
